import { DetailedProductFragment } from '~/generated/graphql';
import { Price } from '~/components/products/Price';
import { calculateEffectivePrice } from '~/utils/pricing';
import { normalizePrice } from '~/utils/normalize-price';

interface RegularOrderPanelProps {
    product: DetailedProductFragment;
    selectedVariant: DetailedProductFragment['variants'][0];
    quantity: number;
    onQuantityChange: (qty: number) => void;
    currencyCode: string;
}

export function RegularOrderPanel({
    product,
    selectedVariant,
    quantity,
    onQuantityChange,
    currencyCode,
}: RegularOrderPanelProps) {
    const priceTiers = selectedVariant?.customFields?.priceTiers || [];

    // Define quick add options based on tiers or defaults
    const quickOptions = priceTiers.length > 0
        ? priceTiers.slice(0, 2).map(tier => tier.minQty)
        : [3, 6];

    return (
        <div className="space-y-4">
            {priceTiers.length > 0 && <div className="text-sm font-medium text-gray-700">Buy more, save more</div>}

            <div className="grid grid-cols-2 gap-3">
                {priceTiers.slice(0, 2).map((tier, idx) => {
                    const priceVal = tier?.price ?? 0;
                    const minQty = tier?.minQty ?? 0;
                    // Tier prices are stored in major units (e.g., 400), need to convert to minor units (40000) for display
                    const price = normalizePrice({ value: priceVal * 100 });
                    if (minQty === 0) return null;

                    return (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => onQuantityChange(minQty)}
                            className={`relative flex flex-col items-center justify-center p-3 border rounded-lg transition-all ${quantity === minQty
                                ? 'border-[#E80245] bg-[#E80245]/5'
                                : 'border-gray-200 hover:border-[#E80245]/50'
                                }`}
                        >
                            <span className="text-lg font-bold text-[#E80245]">
                                <Price priceWithTax={price} currencyCode={currencyCode as any} />
                            </span>
                            <span className="text-xs text-center text-gray-600">
                                for {minQty} pcs+
                            </span>
                            <div className="absolute -top-2 -right-2 bg-[#E80245] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                Add {minQty}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Fallback if no tiers, just minimal layout interaction example? 
                 The prompt specifically asks for "Add 3", "Add 6" buttons. 
                 If tiers exist, we use them. If not, maybe we shouldn't show this section or show generic bulk? 
                 I'll stick to rendering tiers if available. 
             */}
        </div>
    );
}
